// ═══════════════════════════════════════════════════════════════════════════════
// Admin — IP + country_code + "?" HelpPopover (IPinfo Lite details)
// ═══════════════════════════════════════════════════════════════════════════════

import type { IpinfoLiteGeo } from '@fdh/shared';
import { HelpPopover } from '../ui/HelpPopover';

type AdminIpWithGeoProps = {
  ip: string | null | undefined;
  geo: IpinfoLiteGeo | null | undefined;
  className?: string;
};

const GEO_DETAIL_ROWS: Array<{ key: keyof IpinfoLiteGeo; label: string }> = [
  { key: 'ip', label: 'ip' },
  { key: 'countryCode', label: 'country_code' },
  { key: 'country', label: 'country' },
  { key: 'continentCode', label: 'continent_code' },
  { key: 'continent', label: 'continent' },
  { key: 'asn', label: 'asn' },
  { key: 'asName', label: 'as_name' },
  { key: 'asDomain', label: 'as_domain' },
  { key: 'bogon', label: 'bogon' },
  { key: 'anycast', label: 'anycast' },
];

const formatGeoValue = (value: string | boolean | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  const trimmed = value.trim();
  return trimmed === '' ? '—' : trimmed;
};

export const AdminIpWithGeo = ({ ip, geo, className = '' }: AdminIpWithGeoProps) => {
  const displayIp = (ip ?? '').trim();
  if (displayIp === '') {
    return <span className={className || undefined}>—</span>;
  }

  const countryCode = geo?.countryCode?.trim() || null;

  return (
    <span className={`inline-flex max-w-full flex-wrap items-center gap-1.5 ${className}`.trim()}>
      <span className="break-all">{displayIp}</span>
      {countryCode ? (
        <span className="shrink-0 font-medium text-amber-300/90">{countryCode}</span>
      ) : null}
      {geo ? (
        <HelpPopover
          triggerAriaLabel={`IP geolocation details for ${displayIp}`}
          panelAriaLabel="IPinfo Lite details"
          panelWidthClassName="w-80"
          className="align-middle"
        >
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            IPinfo Lite
          </p>
          <dl className="space-y-1.5 text-xs text-slate-700 dark:text-slate-200">
            {GEO_DETAIL_ROWS.map((row) => (
              <div key={row.key} className="grid grid-cols-[7.5rem_1fr] gap-2">
                <dt className="text-slate-500 dark:text-slate-400">{row.label}</dt>
                <dd className="break-all font-mono text-[11px]">
                  {formatGeoValue(geo[row.key])}
                </dd>
              </div>
            ))}
          </dl>
        </HelpPopover>
      ) : null}
    </span>
  );
};
